class BRSelect {
  constructor (name, component) {
    this.name = name
    this.component = component
    this.multiple = component.hasAttribute('multiple')
    this.optionsList = this._setOptionsList()
    this._setBehavior()
  }

  get selected () {
    let selected = []
    for (const [index, option] of this.optionsList.entries()) {
      if (!this.multiple) {
        if (option.selected) {
          selected = option.value
          break
        }
      } else {
        if ((index > 0) && (option.selected)) {
          selected.push(option.value)
        }
      }
    }
    return selected
  }

  _setBehavior () {
    this._setDropdownBehavior()
    this._setKeyboardBehavior()
    this._setSelectionBehavior()
    this._setFilterBehavior()
  }

  _setOptionsList () {
    const optionsList = []
    for (const item of this.component.querySelectorAll('.br-list .item')) {
      for (const label of item.querySelectorAll('.content .br-radio label, .content .br-checkbox label')) {
        const option = {
          value: label.innerText,
          selected: false,
          focus: false,
          visible: true
        }
        optionsList.push(option)
      }
    }
    return optionsList
  }

  _setDropdownBehavior () {
    for (const input of this.component.querySelectorAll('.br-input input[type="text"]')) {
      input.addEventListener('focus', () => {
        this._openSelect()
        this._resetFocus()
      })
    }
    for (const trigger of this.component.querySelectorAll('.br-input button[data-trigger]')) {
      trigger.addEventListener('click', () => {
        for (const list of this.component.querySelectorAll('.br-list')) {
          if (list.hasAttribute('expanded')) {
            this._closeSelect()
          } else {
            this._openSelect()
          }
        }
      })
    }
    window.document.addEventListener('click', (event) => {
      if (!this.component.contains(event.target)) {
        this._closeSelect()
      }
    })
  }

  _setKeyboardBehavior () {
    for (const input of this.component.querySelectorAll('.br-input input[type="text"]')) {
      input.addEventListener('keydown', (event) => {
        if (event.keyCode === 40) {
          event.preventDefault()
          for (const list of this.component.querySelectorAll('.br-list')) {
            list.focus()
            if (list === document.activeElement) {
              this._getNextItem().focus()
            }
          }
        }
      })
    }
    for (const list of this.component.querySelectorAll('.br-list')) {
      list.addEventListener('keydown', (event) => {
        event.preventDefault()
        switch (event.keyCode) {
          case 9:
            this._closeSelect()
            this._resetFocus()
            break
          case 27:
            this._closeSelect()
            break
          case 32:
            for (const [index, item] of list.querySelectorAll('.item').entries()) {
              if (this.optionsList[index].focus) {
                for (const check of item.querySelectorAll('.content .br-radio input[type="radio"], .content .br-checkbox input[type="checkbox"]')) {
                  check.click()
                }
              }
            }
            break
          case 38:
            this._getPreviousItem().focus()
            break
          case 40:
            this._getNextItem().focus()
            break
        }
      })
    }
  }

  _setSelectionBehavior () {
    for (const [index, item] of this.component.querySelectorAll('.br-list .item').entries()) {
      for (const check of item.querySelectorAll('.content .br-radio input[type="radio"], .content .br-checkbox input[type="checkbox"]')) {
        check.addEventListener('click', (event) => {
          if (!this.multiple) {
            for (const [index2, item2] of this.component.querySelectorAll('.br-list .item').entries()) {
              this._removeSelected(index2, item2)
            }
            this._setSelected(index, item)
            this._closeSelect()
          } else if (!event.currentTarget.hasAttribute('checked')) {
            this._setSelected(index, item)
          } else {
            this._removeSelected(index, item)
          }
          if (item.hasAttribute('data-all')) {
            this._setSelectAll(item)
          }
        })
      }
    }
  }

  _setFilterBehavior () {
    for (const input of this.component.querySelectorAll('.br-input input[type="text"]')) {
      input.addEventListener('input', (event) => {
        let allHidden = true
        this._filter(event.currentTarget.value)
        for (const option of this.optionsList) {
          if (option.visible) {
            allHidden = false
          }
        }
        if (allHidden) {
          event.currentTarget.value = event.currentTarget.value.slice(0, -1)
          this._filter(event.currentTarget.value)
        }
      })
    }
  }

  _filter (value) {
    for (const [index, item] of this.component.querySelectorAll('.br-list .item').entries()) {
      if (this.optionsList[index].value.toUpperCase().indexOf(value.toUpperCase()) === -1) {
        item.classList.add('d-none')
        this.optionsList[index].visible = false
      } else {
        item.classList.remove('d-none')
        this.optionsList[index].visible = true
      }
    }
  }

  _setSelectAll (item) {
    for (const check of item.querySelectorAll('.content .br-checkbox input[type="checkbox"]')) {
      if (!check.hasAttribute('checked')) {
        for (const item2 of this.component.querySelectorAll('.br-list .item')) {
          for (const check2 of item2.querySelectorAll('.content .br-checkbox input[type="checkbox"]')) {
            if (check2.hasAttribute('checked')) {
              check2.click()
            }
          }
        }
      } else {
        for (const item2 of this.component.querySelectorAll('.br-list .item')) {
          for (const check2 of item2.querySelectorAll('.content .br-checkbox input[type="checkbox"]')) {
            if (!check2.hasAttribute('checked')) {
              check2.click()
            }
          }
        }
      }
    }
  }

  _setSelected (index, item) {
    item.classList.add('selected')
    for (const check of item.querySelectorAll('.content .br-radio, .content .br-checkbox')) {
      for (const input of check.querySelectorAll('input[type="radio"], input[type="checkbox"]')) {
        input.setAttribute('checked', '')
      }
    }
    this.optionsList[index].selected = true
    this._setInput()
  }

  _removeSelected (index, item) {
    item.classList.remove('selected')
    for (const check of item.querySelectorAll('.content .br-radio, .content .br-checkbox')) {
      for (const input of check.querySelectorAll('input[type="radio"], input[type="checkbox"')) {
        input.removeAttribute('checked')
      }
      this.optionsList[index].selected = false
      this._setInput()
    }
  }

  _setInput () {
    for (const input of this.component.querySelectorAll('.br-input input[type="text"]')) {
      if (!this.multiple) {
        input.value = this.selected
      } else if (this.selected.length === 0) {
        input.value = ''
      } else if (this.selected.length === 1) {
        input.value = this.selected[0]
      } else {
        input.value = this.selected[0] + ' + (' + (this.selected.length - 1) + ')'
      }
    }
  }

  _getNextItem () {
    const list = this.component.querySelectorAll('.br-list .item')
    for (var iFocused = 0; iFocused < this.optionsList.length; iFocused++) {
      if (this.optionsList[iFocused].focus) {
        for (var iVisible = iFocused + 1; iVisible < this.optionsList.length; iVisible++) {
          if (this.optionsList[iVisible].visible) {
            break
          }
        }
        break
      }
    }
    if (iFocused === this.optionsList.length) {
      for (const [index, option] of this.optionsList.entries()) {
        if (option.visible) {
          option.focus = true
          return list[index]
        }
      }
    } else if (iVisible < this.optionsList.length) {
      this.optionsList[iFocused].focus = false
      this.optionsList[iVisible].focus = true
      return list[iVisible]
    } else {
      return list[iFocused]
    }
  }

  _getPreviousItem () {
    const list = this.component.querySelectorAll('.br-list .item')
    for (var iFocused = 0; iFocused < this.optionsList.length; iFocused++) {
      if (this.optionsList[iFocused].focus) {
        for (var iVisible = iFocused - 1; iVisible > 0; iVisible--) {
          if (this.optionsList[iVisible].visible) {
            break
          }
        }
        break
      }
    }
    if (iFocused === 0) {
      return list[iFocused]
    } else {
      this.optionsList[iFocused].focus = false
      this.optionsList[iVisible].focus = true
      return list[iVisible]
    }
  }

  _resetInput () {
    for (const input of this.component.querySelectorAll('.br-input input[type="text"]')) {
      input.value = ''
    }
  }

  _resetFocus () {
    for (const option of this.optionsList) {
      option.focus = false
    }
  }

  _resetVisible () {
    const list = this.component.querySelectorAll('.br-list .item')
    for (const [index, option] of this.optionsList.entries()) {
      option.visible = true
      list[index].classList.remove('d-none')
    }
  }

  _openSelect () {
    for (const list of this.component.querySelectorAll('.br-list')) {
      list.setAttribute('expanded', '')
    }
    for (const icon of this.component.querySelectorAll('.br-input button svg')) {
      icon.classList.remove('fa-angle-down')
      icon.classList.add('fa-angle-up')
    }
    this._resetInput()
  }

  _closeSelect () {
    for (const list of this.component.querySelectorAll('.br-list')) {
      list.removeAttribute('expanded')
    }
    for (const icon of this.component.querySelectorAll('.br-input button svg')) {
      icon.classList.remove('fa-angle-up')
      icon.classList.add('fa-angle-down')
    }
    this._setInput()
    this._resetFocus()
    this._resetVisible()
  }
}

const selectList = []
for (const brSelect of window.document.querySelectorAll('.br-select')) {
  selectList.push(new BRSelect('br-select', brSelect))
}

export default BRSelect
